/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * PACTSWAP & ECDSA & OWNER CM API
 * This API provides endpoints for interacting with PACTSWAP, ECDSA and OWNER compatible modules on the Coinweb network.
 * OpenAPI spec version: 0.6.3
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { orvalMutator } from '../client';

import type {
  BatchGetBalance200,
  BatchGetBalanceParams,
  BatchGetBalanceWithUtxos200,
  BatchGetBalanceWithUtxosParams,
  ComposeFinalizeTx200,
  ComposeFinalizeTxParams,
  ComposeSwapTxByAmountFrom200,
  ComposeSwapTxByAmountFromParams,
  ComposeSwapTxByAmountTo200,
  ComposeSwapTxByAmountToParams,
  ComposeSwapTxByC1OrderId200,
  ComposeSwapTxByC1OrderIdParams,
  GetAllC1Orders200Item,
  GetAllC1OrdersParams,
  GetAllC2Orders200Item,
  GetAllC2OrdersParams,
  GetAllPrices200,
  GetBalance200,
  GetBalanceParams,
  GetBalanceWithUtxos200,
  GetBalanceWithUtxosParams,
  GetDecodedL1TxData200,
  GetDecodedL1TxDataParams,
  GetFeePoolBalance200,
  GetFeePoolBalanceParams,
  GetFeePoolFees200,
  GetFeePoolFees400,
  GetFeePoolFeesParams,
  GetHistoryByHistoryAccess200,
  GetHistoryByHistoryAccessFull200,
  GetHistoryByHistoryAccessFullParams,
  GetHistoryByHistoryAccessParams,
  GetLpCmGetLPList200,
  GetLpCmGetLPListParams,
  GetMakerCollateralBalance200,
  GetMakerCollateralBalanceParams,
  GetPactInfo200,
  GetPactInfoParams,
  GetPubkeyHash200,
  GetPubkeyHashParams,
  GetReversedOrders200Item,
  GetReversedOrdersParams,
  GetSwapQuotesByAmountFrom200Item,
  GetSwapQuotesByAmountFromParams,
  GetSwapQuotesByAmountTo200Item,
  GetSwapQuotesByAmountToParams,
  GetSwapStatus200,
  GetSwapStatusParams,
} from './api.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Composes a swap transaction based on the amount of source tokens you want to send. This endpoint queries available LPs orders, calculates the expected receive amount, and constructs a transaction that can be signed and broadcast to execute the swap. The transaction is composed for the source blockchain and includes all necessary parameters. For Bitcoin-like transactions (BTC, LTC, DOGE), you must provide UTXO information and a public key. The response includes the raw transaction data and optional EVM-specific fields (chainId, value, to, type) for EVM-compatible blockchains. The composed transaction can then be signed by the user's wallet and broadcast to the blockchain to execute the swap.
 * @summary Compose swap transaction based on send amount
 */
export const composeSwapTxByAmountFrom = (params: ComposeSwapTxByAmountFromParams, signal?: AbortSignal) => {
  return orvalMutator<ComposeSwapTxByAmountFrom200>({
    url: `/pactswap_cm/composeSwapTxByAmountFrom`,
    method: 'GET',
    params,
    signal,
  });
};

export const getComposeSwapTxByAmountFromQueryKey = (params?: ComposeSwapTxByAmountFromParams) => {
  return [`/pactswap_cm/composeSwapTxByAmountFrom`, ...(params ? [params] : [])] as const;
};

export const getComposeSwapTxByAmountFromQueryOptions = <
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountFromParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getComposeSwapTxByAmountFromQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>> = ({ signal }) =>
    composeSwapTxByAmountFrom(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ComposeSwapTxByAmountFromQueryResult = NonNullable<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>>;
export type ComposeSwapTxByAmountFromQueryError = unknown;

export function useComposeSwapTxByAmountFrom<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountFromParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
          TError,
          Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeSwapTxByAmountFrom<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountFromParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
          TError,
          Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeSwapTxByAmountFrom<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountFromParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Compose swap transaction based on send amount
 */

export function useComposeSwapTxByAmountFrom<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountFromParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountFrom>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getComposeSwapTxByAmountFromQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Composes a swap transaction based on the amount of destination tokens you want to receive. This endpoint queries available LPs orders, calculates the required source amount, and constructs a transaction that can be signed and broadcast to execute the swap. The transaction is composed for the source blockchain and includes all necessary parameters. For Bitcoin-like transactions (BTC, LTC, DOGE), you must provide UTXO information and a public key. The response includes the raw transaction data and optional EVM-specific fields (chainId, value, to, type) for EVM-compatible blockchains. The composed transaction can then be signed by the user's wallet and broadcast to the blockchain to execute the swap.
 * @summary Compose swap transaction based on desired receive amount
 */
export const composeSwapTxByAmountTo = (params: ComposeSwapTxByAmountToParams, signal?: AbortSignal) => {
  return orvalMutator<ComposeSwapTxByAmountTo200>({
    url: `/pactswap_cm/composeSwapTxByAmountTo`,
    method: 'GET',
    params,
    signal,
  });
};

export const getComposeSwapTxByAmountToQueryKey = (params?: ComposeSwapTxByAmountToParams) => {
  return [`/pactswap_cm/composeSwapTxByAmountTo`, ...(params ? [params] : [])] as const;
};

export const getComposeSwapTxByAmountToQueryOptions = <
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountToParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getComposeSwapTxByAmountToQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>> = ({ signal }) =>
    composeSwapTxByAmountTo(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ComposeSwapTxByAmountToQueryResult = NonNullable<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>>;
export type ComposeSwapTxByAmountToQueryError = unknown;

export function useComposeSwapTxByAmountTo<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountToParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
          TError,
          Awaited<ReturnType<typeof composeSwapTxByAmountTo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeSwapTxByAmountTo<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountToParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
          TError,
          Awaited<ReturnType<typeof composeSwapTxByAmountTo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeSwapTxByAmountTo<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountToParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Compose swap transaction based on desired receive amount
 */

export function useComposeSwapTxByAmountTo<
  TData = Awaited<ReturnType<typeof composeSwapTxByAmountTo>>,
  TError = unknown,
>(
  params: ComposeSwapTxByAmountToParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByAmountTo>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getComposeSwapTxByAmountToQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Composes a swap transaction based on the C1 order ID. This endpoint queries the C1 contract to retrieve the order details and constructs a transaction that can be signed and broadcast to execute the swap. The transaction is composed for the source blockchain and includes all necessary parameters. For Bitcoin-like transactions (BTC, LTC, DOGE), you must provide UTXO information and a public key. The response includes the raw transaction data and optional EVM-specific fields (chainId, value, to, type) for EVM-compatible blockchains. The composed transaction can then be signed by the user's wallet and broadcast to the blockchain to execute the swap.
 * @summary Compose swap transaction based on C1 order ID
 */
export const composeSwapTxByC1OrderId = (params: ComposeSwapTxByC1OrderIdParams, signal?: AbortSignal) => {
  return orvalMutator<ComposeSwapTxByC1OrderId200>({
    url: `/pactswap_cm/composeSwapTxByC1OrderId`,
    method: 'GET',
    params,
    signal,
  });
};

export const getComposeSwapTxByC1OrderIdQueryKey = (params?: ComposeSwapTxByC1OrderIdParams) => {
  return [`/pactswap_cm/composeSwapTxByC1OrderId`, ...(params ? [params] : [])] as const;
};

export const getComposeSwapTxByC1OrderIdQueryOptions = <
  TData = Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
  TError = unknown,
>(
  params: ComposeSwapTxByC1OrderIdParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getComposeSwapTxByC1OrderIdQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>> = ({ signal }) =>
    composeSwapTxByC1OrderId(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ComposeSwapTxByC1OrderIdQueryResult = NonNullable<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>>;
export type ComposeSwapTxByC1OrderIdQueryError = unknown;

export function useComposeSwapTxByC1OrderId<
  TData = Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
  TError = unknown,
>(
  params: ComposeSwapTxByC1OrderIdParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
          TError,
          Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeSwapTxByC1OrderId<
  TData = Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
  TError = unknown,
>(
  params: ComposeSwapTxByC1OrderIdParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
          TError,
          Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeSwapTxByC1OrderId<
  TData = Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
  TError = unknown,
>(
  params: ComposeSwapTxByC1OrderIdParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Compose swap transaction based on C1 order ID
 */

export function useComposeSwapTxByC1OrderId<
  TData = Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>,
  TError = unknown,
>(
  params: ComposeSwapTxByC1OrderIdParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeSwapTxByC1OrderId>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getComposeSwapTxByC1OrderIdQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Composes a finalization transaction for a specific execution request (PACT). This endpoint is used by market makers to finalize a swap by sending the destination tokens to the user. The transaction is composed for the swap destination blockchain. For Bitcoin-like transactions (BTC, LTC, DOGE), you must provide UTXO information and a public key. The response includes the raw transaction data and optional EVM-specific fields (chainId, value, to, type) for EVM-compatible blockchains. The composed transaction can then be signed by the market maker's wallet and broadcast to the blockchain to finalize the swap and complete the execution request.
 * @summary Compose transaction to finalize a PACT
 */
export const composeFinalizeTx = (params: ComposeFinalizeTxParams, signal?: AbortSignal) => {
  return orvalMutator<ComposeFinalizeTx200>({ url: `/pactswap_cm/composeFinalizeTx`, method: 'GET', params, signal });
};

export const getComposeFinalizeTxQueryKey = (params?: ComposeFinalizeTxParams) => {
  return [`/pactswap_cm/composeFinalizeTx`, ...(params ? [params] : [])] as const;
};

export const getComposeFinalizeTxQueryOptions = <
  TData = Awaited<ReturnType<typeof composeFinalizeTx>>,
  TError = unknown,
>(
  params: ComposeFinalizeTxParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeFinalizeTx>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getComposeFinalizeTxQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof composeFinalizeTx>>> = ({ signal }) =>
    composeFinalizeTx(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof composeFinalizeTx>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ComposeFinalizeTxQueryResult = NonNullable<Awaited<ReturnType<typeof composeFinalizeTx>>>;
export type ComposeFinalizeTxQueryError = unknown;

export function useComposeFinalizeTx<TData = Awaited<ReturnType<typeof composeFinalizeTx>>, TError = unknown>(
  params: ComposeFinalizeTxParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeFinalizeTx>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeFinalizeTx>>,
          TError,
          Awaited<ReturnType<typeof composeFinalizeTx>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeFinalizeTx<TData = Awaited<ReturnType<typeof composeFinalizeTx>>, TError = unknown>(
  params: ComposeFinalizeTxParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeFinalizeTx>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof composeFinalizeTx>>,
          TError,
          Awaited<ReturnType<typeof composeFinalizeTx>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useComposeFinalizeTx<TData = Awaited<ReturnType<typeof composeFinalizeTx>>, TError = unknown>(
  params: ComposeFinalizeTxParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeFinalizeTx>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Compose transaction to finalize a PACT
 */

export function useComposeFinalizeTx<TData = Awaited<ReturnType<typeof composeFinalizeTx>>, TError = unknown>(
  params: ComposeFinalizeTxParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof composeFinalizeTx>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getComposeFinalizeTxQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves multiple swap quotes from available LPs based on the amount of source tokens you want to send. This endpoint queries active LP orders and calculates how much of the destination token you will receive for the specified amount of source tokens. Returns an array of quotes, each containing the expected receive amount (amountTo) and optionally the LP address. This is useful when you know exactly how much you want to send and need to compare different LP rates to see which offers the best return. The quotes are typically sorted by the best rate (highest amountTo for the same amountFrom).
 * @summary Get multiple swap quotes based on send amount
 */
export const getSwapQuotesByAmountFrom = (params: GetSwapQuotesByAmountFromParams, signal?: AbortSignal) => {
  return orvalMutator<GetSwapQuotesByAmountFrom200Item[]>({
    url: `/pactswap_cm/getSwapQuotesByAmountFrom`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetSwapQuotesByAmountFromQueryKey = (params?: GetSwapQuotesByAmountFromParams) => {
  return [`/pactswap_cm/getSwapQuotesByAmountFrom`, ...(params ? [params] : [])] as const;
};

export const getGetSwapQuotesByAmountFromQueryOptions = <
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountFromParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSwapQuotesByAmountFromQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>> = ({ signal }) =>
    getSwapQuotesByAmountFrom(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSwapQuotesByAmountFromQueryResult = NonNullable<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>>;
export type GetSwapQuotesByAmountFromQueryError = unknown;

export function useGetSwapQuotesByAmountFrom<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountFromParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
          TError,
          Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSwapQuotesByAmountFrom<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountFromParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
          TError,
          Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSwapQuotesByAmountFrom<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountFromParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get multiple swap quotes based on send amount
 */

export function useGetSwapQuotesByAmountFrom<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountFromParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountFrom>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSwapQuotesByAmountFromQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves multiple swap quotes from available LPs based on the amount of destination tokens you want to receive. This endpoint queries active LP orders and calculates how much of the source token you need to send to receive the specified amount of destination tokens. Returns an array of quotes, each containing the required source amount (amountFrom) and optionally the LP address. This is useful when you know exactly how much you want to receive and need to compare different LP rates. The quotes are typically sorted by the best rate (lowest amountFrom for the same amountTo).
 * @summary Get multiple swap quotes based on desired receive amount
 */
export const getSwapQuotesByAmountTo = (params: GetSwapQuotesByAmountToParams, signal?: AbortSignal) => {
  return orvalMutator<GetSwapQuotesByAmountTo200Item[]>({
    url: `/pactswap_cm/getSwapQuotesByAmountTo`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetSwapQuotesByAmountToQueryKey = (params?: GetSwapQuotesByAmountToParams) => {
  return [`/pactswap_cm/getSwapQuotesByAmountTo`, ...(params ? [params] : [])] as const;
};

export const getGetSwapQuotesByAmountToQueryOptions = <
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountToParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSwapQuotesByAmountToQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>> = ({ signal }) =>
    getSwapQuotesByAmountTo(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSwapQuotesByAmountToQueryResult = NonNullable<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>>;
export type GetSwapQuotesByAmountToQueryError = unknown;

export function useGetSwapQuotesByAmountTo<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountToParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
          TError,
          Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSwapQuotesByAmountTo<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountToParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
          TError,
          Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSwapQuotesByAmountTo<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountToParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get multiple swap quotes based on desired receive amount
 */

export function useGetSwapQuotesByAmountTo<
  TData = Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>,
  TError = unknown,
>(
  params: GetSwapQuotesByAmountToParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapQuotesByAmountTo>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSwapQuotesByAmountToQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the status of a swap transaction and returns the L1 transaction ID of the receiving transaction (toL1TxId) if the swap has been finalized. For BTC, LTC, and DOGE transactions, you can optionally provide the receiverAddress and toType to check for unconfirmed finalization transactions that may not yet be confirmed on the blockchain. The toL1TxId is returned when the swap has been successfully finalized.
 * @summary Get swap execution status
 */
export const getSwapStatus = (params: GetSwapStatusParams, signal?: AbortSignal) => {
  return orvalMutator<GetSwapStatus200>({ url: `/pactswap_cm/getSwapStatus`, method: 'GET', params, signal });
};

export const getGetSwapStatusQueryKey = (params?: GetSwapStatusParams) => {
  return [`/pactswap_cm/getSwapStatus`, ...(params ? [params] : [])] as const;
};

export const getGetSwapStatusQueryOptions = <TData = Awaited<ReturnType<typeof getSwapStatus>>, TError = unknown>(
  params: GetSwapStatusParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapStatus>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSwapStatusQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSwapStatus>>> = ({ signal }) =>
    getSwapStatus(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSwapStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSwapStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getSwapStatus>>>;
export type GetSwapStatusQueryError = unknown;

export function useGetSwapStatus<TData = Awaited<ReturnType<typeof getSwapStatus>>, TError = unknown>(
  params: GetSwapStatusParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSwapStatus>>,
          TError,
          Awaited<ReturnType<typeof getSwapStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSwapStatus<TData = Awaited<ReturnType<typeof getSwapStatus>>, TError = unknown>(
  params: GetSwapStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSwapStatus>>,
          TError,
          Awaited<ReturnType<typeof getSwapStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSwapStatus<TData = Awaited<ReturnType<typeof getSwapStatus>>, TError = unknown>(
  params: GetSwapStatusParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapStatus>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get swap execution status
 */

export function useGetSwapStatus<TData = Awaited<ReturnType<typeof getSwapStatus>>, TError = unknown>(
  params: GetSwapStatusParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSwapStatus>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSwapStatusQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves detailed fee pool information for a specific swap transaction identified by the L1 transaction ID. This endpoint queries the contract state to retrieve all fee-related amounts in CWEB tokens, including amounts sent to the fee pool, fees charged by contracts, amounts locked by execution requests (PACTs), and the fee receiver address. This information is useful for tracking fee distribution and understanding the cost breakdown of a swap transaction.
 * @summary Get FeePool fees for a swap
 */
export const getFeePoolFees = (params: GetFeePoolFeesParams, signal?: AbortSignal) => {
  return orvalMutator<GetFeePoolFees200>({ url: `/pactswap_cm/getFeePoolFees`, method: 'GET', params, signal });
};

export const getGetFeePoolFeesQueryKey = (params?: GetFeePoolFeesParams) => {
  return [`/pactswap_cm/getFeePoolFees`, ...(params ? [params] : [])] as const;
};

export const getGetFeePoolFeesQueryOptions = <
  TData = Awaited<ReturnType<typeof getFeePoolFees>>,
  TError = GetFeePoolFees400,
>(
  params: GetFeePoolFeesParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolFees>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFeePoolFeesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFeePoolFees>>> = ({ signal }) =>
    getFeePoolFees(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getFeePoolFees>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetFeePoolFeesQueryResult = NonNullable<Awaited<ReturnType<typeof getFeePoolFees>>>;
export type GetFeePoolFeesQueryError = GetFeePoolFees400;

export function useGetFeePoolFees<TData = Awaited<ReturnType<typeof getFeePoolFees>>, TError = GetFeePoolFees400>(
  params: GetFeePoolFeesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolFees>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFeePoolFees>>,
          TError,
          Awaited<ReturnType<typeof getFeePoolFees>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFeePoolFees<TData = Awaited<ReturnType<typeof getFeePoolFees>>, TError = GetFeePoolFees400>(
  params: GetFeePoolFeesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolFees>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFeePoolFees>>,
          TError,
          Awaited<ReturnType<typeof getFeePoolFees>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFeePoolFees<TData = Awaited<ReturnType<typeof getFeePoolFees>>, TError = GetFeePoolFees400>(
  params: GetFeePoolFeesParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolFees>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get FeePool fees for a swap
 */

export function useGetFeePoolFees<TData = Awaited<ReturnType<typeof getFeePoolFees>>, TError = GetFeePoolFees400>(
  params: GetFeePoolFeesParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolFees>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFeePoolFeesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves all C1 orders from the specified C1 token contract. C1 orders are bid orders where market makers sell CWEB for L1 tokens. The response includes all orders regardless of their activity status (ACTIVE, COMPLETED, CANCELLING, CANCELLED, EXPIRED, ERROR) or payment status (PAYABLE, NOT_PAYABLE, PAID).
 * @summary Retrieve all orders of C1 token contract
 */
export const getAllC1Orders = (params: GetAllC1OrdersParams, signal?: AbortSignal) => {
  return orvalMutator<GetAllC1Orders200Item[]>({ url: `/pactswap_cm/getAllC1Orders`, method: 'GET', params, signal });
};

export const getGetAllC1OrdersQueryKey = (params?: GetAllC1OrdersParams) => {
  return [`/pactswap_cm/getAllC1Orders`, ...(params ? [params] : [])] as const;
};

export const getGetAllC1OrdersQueryOptions = <TData = Awaited<ReturnType<typeof getAllC1Orders>>, TError = unknown>(
  params: GetAllC1OrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC1Orders>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllC1OrdersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllC1Orders>>> = ({ signal }) =>
    getAllC1Orders(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllC1Orders>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllC1OrdersQueryResult = NonNullable<Awaited<ReturnType<typeof getAllC1Orders>>>;
export type GetAllC1OrdersQueryError = unknown;

export function useGetAllC1Orders<TData = Awaited<ReturnType<typeof getAllC1Orders>>, TError = unknown>(
  params: GetAllC1OrdersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC1Orders>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllC1Orders>>,
          TError,
          Awaited<ReturnType<typeof getAllC1Orders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllC1Orders<TData = Awaited<ReturnType<typeof getAllC1Orders>>, TError = unknown>(
  params: GetAllC1OrdersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC1Orders>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllC1Orders>>,
          TError,
          Awaited<ReturnType<typeof getAllC1Orders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllC1Orders<TData = Awaited<ReturnType<typeof getAllC1Orders>>, TError = unknown>(
  params: GetAllC1OrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC1Orders>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve all orders of C1 token contract
 */

export function useGetAllC1Orders<TData = Awaited<ReturnType<typeof getAllC1Orders>>, TError = unknown>(
  params: GetAllC1OrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC1Orders>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllC1OrdersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves all C2 market maker orders from the specified C2 token contract. C2 orders are Ask orders where market makers sell L1 tokens for CWEB. The response includes all orders regardless of their activity status (ACTIVE, PENDING, or DISABLED).
 * @summary Retrieve all orders of C2 token contract
 */
export const getAllC2Orders = (params: GetAllC2OrdersParams, signal?: AbortSignal) => {
  return orvalMutator<GetAllC2Orders200Item[]>({ url: `/pactswap_cm/getAllC2Orders`, method: 'GET', params, signal });
};

export const getGetAllC2OrdersQueryKey = (params?: GetAllC2OrdersParams) => {
  return [`/pactswap_cm/getAllC2Orders`, ...(params ? [params] : [])] as const;
};

export const getGetAllC2OrdersQueryOptions = <TData = Awaited<ReturnType<typeof getAllC2Orders>>, TError = unknown>(
  params: GetAllC2OrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC2Orders>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllC2OrdersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllC2Orders>>> = ({ signal }) =>
    getAllC2Orders(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllC2Orders>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllC2OrdersQueryResult = NonNullable<Awaited<ReturnType<typeof getAllC2Orders>>>;
export type GetAllC2OrdersQueryError = unknown;

export function useGetAllC2Orders<TData = Awaited<ReturnType<typeof getAllC2Orders>>, TError = unknown>(
  params: GetAllC2OrdersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC2Orders>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllC2Orders>>,
          TError,
          Awaited<ReturnType<typeof getAllC2Orders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllC2Orders<TData = Awaited<ReturnType<typeof getAllC2Orders>>, TError = unknown>(
  params: GetAllC2OrdersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC2Orders>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllC2Orders>>,
          TError,
          Awaited<ReturnType<typeof getAllC2Orders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllC2Orders<TData = Awaited<ReturnType<typeof getAllC2Orders>>, TError = unknown>(
  params: GetAllC2OrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC2Orders>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve all orders of C2 token contract
 */

export function useGetAllC2Orders<TData = Awaited<ReturnType<typeof getAllC2Orders>>, TError = unknown>(
  params: GetAllC2OrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllC2Orders>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllC2OrdersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves all Reversed C1 orders from the specified C1 token contract address. Reversed orders are created by C2 contract when it can't find a C2 order for a swap, or when the expired PACT is converted to a Reversed order.
 * @summary Retrieve all Reversed orders of C1 token contract
 */
export const getReversedOrders = (params: GetReversedOrdersParams, signal?: AbortSignal) => {
  return orvalMutator<GetReversedOrders200Item[]>({
    url: `/pactswap_cm/getReversedOrders`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetReversedOrdersQueryKey = (params?: GetReversedOrdersParams) => {
  return [`/pactswap_cm/getReversedOrders`, ...(params ? [params] : [])] as const;
};

export const getGetReversedOrdersQueryOptions = <
  TData = Awaited<ReturnType<typeof getReversedOrders>>,
  TError = unknown,
>(
  params: GetReversedOrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReversedOrders>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReversedOrdersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReversedOrders>>> = ({ signal }) =>
    getReversedOrders(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReversedOrders>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetReversedOrdersQueryResult = NonNullable<Awaited<ReturnType<typeof getReversedOrders>>>;
export type GetReversedOrdersQueryError = unknown;

export function useGetReversedOrders<TData = Awaited<ReturnType<typeof getReversedOrders>>, TError = unknown>(
  params: GetReversedOrdersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReversedOrders>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReversedOrders>>,
          TError,
          Awaited<ReturnType<typeof getReversedOrders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReversedOrders<TData = Awaited<ReturnType<typeof getReversedOrders>>, TError = unknown>(
  params: GetReversedOrdersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReversedOrders>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReversedOrders>>,
          TError,
          Awaited<ReturnType<typeof getReversedOrders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReversedOrders<TData = Awaited<ReturnType<typeof getReversedOrders>>, TError = unknown>(
  params: GetReversedOrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReversedOrders>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve all Reversed orders of C1 token contract
 */

export function useGetReversedOrders<TData = Awaited<ReturnType<typeof getReversedOrders>>, TError = unknown>(
  params: GetReversedOrdersParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReversedOrders>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetReversedOrdersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves complete information about an execution request (PACT) identified by an L1 transaction ID. A PACT is an inquiry to the market makers to send destination L1 to the user.
 * @summary Get PACT of a swap
 */
export const getPactInfo = (params: GetPactInfoParams, signal?: AbortSignal) => {
  return orvalMutator<GetPactInfo200>({ url: `/pactswap_cm/getPactInfo`, method: 'GET', params, signal });
};

export const getGetPactInfoQueryKey = (params?: GetPactInfoParams) => {
  return [`/pactswap_cm/getPactInfo`, ...(params ? [params] : [])] as const;
};

export const getGetPactInfoQueryOptions = <TData = Awaited<ReturnType<typeof getPactInfo>>, TError = unknown>(
  params: GetPactInfoParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPactInfo>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPactInfoQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPactInfo>>> = ({ signal }) => getPactInfo(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPactInfo>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPactInfoQueryResult = NonNullable<Awaited<ReturnType<typeof getPactInfo>>>;
export type GetPactInfoQueryError = unknown;

export function useGetPactInfo<TData = Awaited<ReturnType<typeof getPactInfo>>, TError = unknown>(
  params: GetPactInfoParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPactInfo>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPactInfo>>,
          TError,
          Awaited<ReturnType<typeof getPactInfo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPactInfo<TData = Awaited<ReturnType<typeof getPactInfo>>, TError = unknown>(
  params: GetPactInfoParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPactInfo>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPactInfo>>,
          TError,
          Awaited<ReturnType<typeof getPactInfo>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPactInfo<TData = Awaited<ReturnType<typeof getPactInfo>>, TError = unknown>(
  params: GetPactInfoParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPactInfo>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get PACT of a swap
 */

export function useGetPactInfo<TData = Awaited<ReturnType<typeof getPactInfo>>, TError = unknown>(
  params: GetPactInfoParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPactInfo>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPactInfoQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Decodes raw L1 transaction data to extract swap execution parameters. This endpoint queries the key-value storage contract to retrieve and decode transaction information, extracting details such as minimum L1 amount, promised L1 amount, recipient addresses, and optional fallback recipient. The decoded data is used to understand swap execution requirements and validate transaction parameters. Returns null if the transaction data cannot be decoded or does not exist in storage.
 * @summary Decode L1 transaction data
 */
export const getDecodedL1TxData = (params: GetDecodedL1TxDataParams, signal?: AbortSignal) => {
  return orvalMutator<GetDecodedL1TxData200>({ url: `/pactswap_cm/getDecodedL1TxData`, method: 'GET', params, signal });
};

export const getGetDecodedL1TxDataQueryKey = (params?: GetDecodedL1TxDataParams) => {
  return [`/pactswap_cm/getDecodedL1TxData`, ...(params ? [params] : [])] as const;
};

export const getGetDecodedL1TxDataQueryOptions = <
  TData = Awaited<ReturnType<typeof getDecodedL1TxData>>,
  TError = unknown,
>(
  params: GetDecodedL1TxDataParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDecodedL1TxData>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDecodedL1TxDataQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDecodedL1TxData>>> = ({ signal }) =>
    getDecodedL1TxData(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDecodedL1TxData>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDecodedL1TxDataQueryResult = NonNullable<Awaited<ReturnType<typeof getDecodedL1TxData>>>;
export type GetDecodedL1TxDataQueryError = unknown;

export function useGetDecodedL1TxData<TData = Awaited<ReturnType<typeof getDecodedL1TxData>>, TError = unknown>(
  params: GetDecodedL1TxDataParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDecodedL1TxData>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDecodedL1TxData>>,
          TError,
          Awaited<ReturnType<typeof getDecodedL1TxData>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDecodedL1TxData<TData = Awaited<ReturnType<typeof getDecodedL1TxData>>, TError = unknown>(
  params: GetDecodedL1TxDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDecodedL1TxData>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDecodedL1TxData>>,
          TError,
          Awaited<ReturnType<typeof getDecodedL1TxData>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDecodedL1TxData<TData = Awaited<ReturnType<typeof getDecodedL1TxData>>, TError = unknown>(
  params: GetDecodedL1TxDataParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDecodedL1TxData>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Decode L1 transaction data
 */

export function useGetDecodedL1TxData<TData = Awaited<ReturnType<typeof getDecodedL1TxData>>, TError = unknown>(
  params: GetDecodedL1TxDataParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDecodedL1TxData>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDecodedL1TxDataQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets the hash of a public key using the SHA-256 algorithm.
 * @summary Get public key hash
 */
export const getPubkeyHash = (params: GetPubkeyHashParams, signal?: AbortSignal) => {
  return orvalMutator<GetPubkeyHash200>({ url: `/pactswap_cm/getPubkeyHash`, method: 'GET', params, signal });
};

export const getGetPubkeyHashQueryKey = (params?: GetPubkeyHashParams) => {
  return [`/pactswap_cm/getPubkeyHash`, ...(params ? [params] : [])] as const;
};

export const getGetPubkeyHashQueryOptions = <TData = Awaited<ReturnType<typeof getPubkeyHash>>, TError = unknown>(
  params: GetPubkeyHashParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPubkeyHash>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPubkeyHashQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPubkeyHash>>> = ({ signal }) =>
    getPubkeyHash(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPubkeyHash>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPubkeyHashQueryResult = NonNullable<Awaited<ReturnType<typeof getPubkeyHash>>>;
export type GetPubkeyHashQueryError = unknown;

export function useGetPubkeyHash<TData = Awaited<ReturnType<typeof getPubkeyHash>>, TError = unknown>(
  params: GetPubkeyHashParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPubkeyHash>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPubkeyHash>>,
          TError,
          Awaited<ReturnType<typeof getPubkeyHash>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPubkeyHash<TData = Awaited<ReturnType<typeof getPubkeyHash>>, TError = unknown>(
  params: GetPubkeyHashParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPubkeyHash>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPubkeyHash>>,
          TError,
          Awaited<ReturnType<typeof getPubkeyHash>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPubkeyHash<TData = Awaited<ReturnType<typeof getPubkeyHash>>, TError = unknown>(
  params: GetPubkeyHashParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPubkeyHash>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get public key hash
 */

export function useGetPubkeyHash<TData = Awaited<ReturnType<typeof getPubkeyHash>>, TError = unknown>(
  params: GetPubkeyHashParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPubkeyHash>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPubkeyHashQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves all prices for all tokens.
 * @summary Get all prices
 */
export const getAllPrices = (signal?: AbortSignal) => {
  return orvalMutator<GetAllPrices200>({ url: `/pactswap_cm/getAllPrices`, method: 'GET', signal });
};

export const getGetAllPricesQueryKey = () => {
  return [`/pactswap_cm/getAllPrices`] as const;
};

export const getGetAllPricesQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllPrices>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllPrices>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllPricesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllPrices>>> = ({ signal }) => getAllPrices(signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllPrices>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllPricesQueryResult = NonNullable<Awaited<ReturnType<typeof getAllPrices>>>;
export type GetAllPricesQueryError = unknown;

export function useGetAllPrices<TData = Awaited<ReturnType<typeof getAllPrices>>, TError = unknown>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllPrices>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllPrices>>,
          TError,
          Awaited<ReturnType<typeof getAllPrices>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllPrices<TData = Awaited<ReturnType<typeof getAllPrices>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllPrices>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllPrices>>,
          TError,
          Awaited<ReturnType<typeof getAllPrices>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllPrices<TData = Awaited<ReturnType<typeof getAllPrices>>, TError = unknown>(
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllPrices>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all prices
 */

export function useGetAllPrices<TData = Awaited<ReturnType<typeof getAllPrices>>, TError = unknown>(
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllPrices>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllPricesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the history by history access
 * @summary Get history by history access
 */
export const getHistoryByHistoryAccess = (params: GetHistoryByHistoryAccessParams, signal?: AbortSignal) => {
  return orvalMutator<GetHistoryByHistoryAccess200>({
    url: `/pactswap_cm/getHistoryAccess`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetHistoryByHistoryAccessQueryKey = (params?: GetHistoryByHistoryAccessParams) => {
  return [`/pactswap_cm/getHistoryAccess`, ...(params ? [params] : [])] as const;
};

export const getGetHistoryByHistoryAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHistoryByHistoryAccessQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>> = ({ signal }) =>
    getHistoryByHistoryAccess(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHistoryByHistoryAccessQueryResult = NonNullable<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>>;
export type GetHistoryByHistoryAccessQueryError = unknown;

export function useGetHistoryByHistoryAccess<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
          TError,
          Awaited<ReturnType<typeof getHistoryByHistoryAccess>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHistoryByHistoryAccess<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
          TError,
          Awaited<ReturnType<typeof getHistoryByHistoryAccess>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHistoryByHistoryAccess<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get history by history access
 */

export function useGetHistoryByHistoryAccess<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccess>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccess>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHistoryByHistoryAccessQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the history by history access full
 * @summary Get history by history access full
 */
export const getHistoryByHistoryAccessFull = (params: GetHistoryByHistoryAccessFullParams, signal?: AbortSignal) => {
  return orvalMutator<GetHistoryByHistoryAccessFull200>({
    url: `/pactswap_cm/getHistoryByHistoryAccess`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetHistoryByHistoryAccessFullQueryKey = (params?: GetHistoryByHistoryAccessFullParams) => {
  return [`/pactswap_cm/getHistoryByHistoryAccess`, ...(params ? [params] : [])] as const;
};

export const getGetHistoryByHistoryAccessFullQueryOptions = <
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessFullParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHistoryByHistoryAccessFullQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>> = ({ signal }) =>
    getHistoryByHistoryAccessFull(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHistoryByHistoryAccessFullQueryResult = NonNullable<
  Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>
>;
export type GetHistoryByHistoryAccessFullQueryError = unknown;

export function useGetHistoryByHistoryAccessFull<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessFullParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
          TError,
          Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHistoryByHistoryAccessFull<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessFullParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
          TError,
          Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHistoryByHistoryAccessFull<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessFullParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get history by history access full
 */

export function useGetHistoryByHistoryAccessFull<
  TData = Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>,
  TError = unknown,
>(
  params: GetHistoryByHistoryAccessFullParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHistoryByHistoryAccessFull>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHistoryByHistoryAccessFullQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the collateral balance for a market maker identified by its public key and token contract address.
 * @summary Get market maker collateral balance
 */
export const getMakerCollateralBalance = (params: GetMakerCollateralBalanceParams, signal?: AbortSignal) => {
  return orvalMutator<GetMakerCollateralBalance200>({
    url: `/pactswap_cm/getMakerCollateralBalance`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetMakerCollateralBalanceQueryKey = (params?: GetMakerCollateralBalanceParams) => {
  return [`/pactswap_cm/getMakerCollateralBalance`, ...(params ? [params] : [])] as const;
};

export const getGetMakerCollateralBalanceQueryOptions = <
  TData = Awaited<ReturnType<typeof getMakerCollateralBalance>>,
  TError = unknown,
>(
  params: GetMakerCollateralBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMakerCollateralBalance>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMakerCollateralBalanceQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMakerCollateralBalance>>> = ({ signal }) =>
    getMakerCollateralBalance(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMakerCollateralBalance>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMakerCollateralBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof getMakerCollateralBalance>>>;
export type GetMakerCollateralBalanceQueryError = unknown;

export function useGetMakerCollateralBalance<
  TData = Awaited<ReturnType<typeof getMakerCollateralBalance>>,
  TError = unknown,
>(
  params: GetMakerCollateralBalanceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMakerCollateralBalance>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMakerCollateralBalance>>,
          TError,
          Awaited<ReturnType<typeof getMakerCollateralBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMakerCollateralBalance<
  TData = Awaited<ReturnType<typeof getMakerCollateralBalance>>,
  TError = unknown,
>(
  params: GetMakerCollateralBalanceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMakerCollateralBalance>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMakerCollateralBalance>>,
          TError,
          Awaited<ReturnType<typeof getMakerCollateralBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMakerCollateralBalance<
  TData = Awaited<ReturnType<typeof getMakerCollateralBalance>>,
  TError = unknown,
>(
  params: GetMakerCollateralBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMakerCollateralBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get market maker collateral balance
 */

export function useGetMakerCollateralBalance<
  TData = Awaited<ReturnType<typeof getMakerCollateralBalance>>,
  TError = unknown,
>(
  params: GetMakerCollateralBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMakerCollateralBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMakerCollateralBalanceQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the CWEB token balance for an ECDSA wallet identified by its public key, including detailed information about all unspent transaction outputs (UTXOs). This endpoint queries the blockchain to get the current balance and all UTXOs associated with the wallet. The UTXOs are sorted by balance in descending order (highest first), which is useful for transaction construction. This endpoint is ideal when you need detailed UTXO information for building transactions that require specific UTXO selection.
 * @summary Get CWEB wallet balance with UTXO details
 */
export const getBalanceWithUtxos = (params: GetBalanceWithUtxosParams, signal?: AbortSignal) => {
  return orvalMutator<GetBalanceWithUtxos200>({
    url: `/ecdsa_cm/getWalletBalanceCWEBwithUTXOs`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetBalanceWithUtxosQueryKey = (params?: GetBalanceWithUtxosParams) => {
  return [`/ecdsa_cm/getWalletBalanceCWEBwithUTXOs`, ...(params ? [params] : [])] as const;
};

export const getGetBalanceWithUtxosQueryOptions = <
  TData = Awaited<ReturnType<typeof getBalanceWithUtxos>>,
  TError = unknown,
>(
  params: GetBalanceWithUtxosParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBalanceWithUtxosQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBalanceWithUtxos>>> = ({ signal }) =>
    getBalanceWithUtxos(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBalanceWithUtxos>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBalanceWithUtxosQueryResult = NonNullable<Awaited<ReturnType<typeof getBalanceWithUtxos>>>;
export type GetBalanceWithUtxosQueryError = unknown;

export function useGetBalanceWithUtxos<TData = Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError = unknown>(
  params: GetBalanceWithUtxosParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBalanceWithUtxos>>,
          TError,
          Awaited<ReturnType<typeof getBalanceWithUtxos>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBalanceWithUtxos<TData = Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError = unknown>(
  params: GetBalanceWithUtxosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBalanceWithUtxos>>,
          TError,
          Awaited<ReturnType<typeof getBalanceWithUtxos>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBalanceWithUtxos<TData = Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError = unknown>(
  params: GetBalanceWithUtxosParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get CWEB wallet balance with UTXO details
 */

export function useGetBalanceWithUtxos<TData = Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError = unknown>(
  params: GetBalanceWithUtxosParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalanceWithUtxos>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBalanceWithUtxosQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the CWEB token balance for an ECDSA wallet identified by its public key, without detailed UTXO information. This endpoint queries the blockchain to get the current balance, total UTXO count, and balance in hex format, but does not return the individual UTXO details. This is more efficient when you only need the total balance and don't require UTXO-level information for transaction construction.
 * @summary Get CWEB wallet balance without UTXO details
 */
export const getBalance = (params: GetBalanceParams, signal?: AbortSignal) => {
  return orvalMutator<GetBalance200>({ url: `/ecdsa_cm/getWalletBalanceCWEB`, method: 'GET', params, signal });
};

export const getGetBalanceQueryKey = (params?: GetBalanceParams) => {
  return [`/ecdsa_cm/getWalletBalanceCWEB`, ...(params ? [params] : [])] as const;
};

export const getGetBalanceQueryOptions = <TData = Awaited<ReturnType<typeof getBalance>>, TError = unknown>(
  params: GetBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalance>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBalanceQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBalance>>> = ({ signal }) => getBalance(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBalance>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof getBalance>>>;
export type GetBalanceQueryError = unknown;

export function useGetBalance<TData = Awaited<ReturnType<typeof getBalance>>, TError = unknown>(
  params: GetBalanceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalance>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBalance>>,
          TError,
          Awaited<ReturnType<typeof getBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBalance<TData = Awaited<ReturnType<typeof getBalance>>, TError = unknown>(
  params: GetBalanceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalance>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBalance>>,
          TError,
          Awaited<ReturnType<typeof getBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBalance<TData = Awaited<ReturnType<typeof getBalance>>, TError = unknown>(
  params: GetBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get CWEB wallet balance without UTXO details
 */

export function useGetBalance<TData = Awaited<ReturnType<typeof getBalance>>, TError = unknown>(
  params: GetBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBalanceQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves CWEB token balances for multiple ECDSA wallets identified by their public keys, including detailed information about all unspent transaction outputs (UTXOs) for each wallet. This endpoint queries the blockchain for multiple wallets in a single request, which is more efficient than making individual requests. The response is a record mapping each public key to its balance information with UTXO details. UTXOs for each wallet are sorted by balance in descending order (highest first). This endpoint is ideal when you need to check balances and UTXO details for multiple wallets simultaneously.
 * @summary Batch get CWEB wallet balances with UTXO details
 */
export const batchGetBalanceWithUtxos = (params: BatchGetBalanceWithUtxosParams, signal?: AbortSignal) => {
  return orvalMutator<BatchGetBalanceWithUtxos200>({
    url: `/ecdsa_cm/batchGetWalletBalanceCWEBwithUTXOs`,
    method: 'GET',
    params,
    signal,
  });
};

export const getBatchGetBalanceWithUtxosQueryKey = (params?: BatchGetBalanceWithUtxosParams) => {
  return [`/ecdsa_cm/batchGetWalletBalanceCWEBwithUTXOs`, ...(params ? [params] : [])] as const;
};

export const getBatchGetBalanceWithUtxosQueryOptions = <
  TData = Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
  TError = unknown,
>(
  params: BatchGetBalanceWithUtxosParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBatchGetBalanceWithUtxosQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>> = ({ signal }) =>
    batchGetBalanceWithUtxos(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BatchGetBalanceWithUtxosQueryResult = NonNullable<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>>;
export type BatchGetBalanceWithUtxosQueryError = unknown;

export function useBatchGetBalanceWithUtxos<
  TData = Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
  TError = unknown,
>(
  params: BatchGetBalanceWithUtxosParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
          TError,
          Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBatchGetBalanceWithUtxos<
  TData = Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
  TError = unknown,
>(
  params: BatchGetBalanceWithUtxosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
          TError,
          Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBatchGetBalanceWithUtxos<
  TData = Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
  TError = unknown,
>(
  params: BatchGetBalanceWithUtxosParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Batch get CWEB wallet balances with UTXO details
 */

export function useBatchGetBalanceWithUtxos<
  TData = Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>,
  TError = unknown,
>(
  params: BatchGetBalanceWithUtxosParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalanceWithUtxos>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBatchGetBalanceWithUtxosQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves CWEB token balances for multiple ECDSA wallets identified by their public keys, without detailed UTXO information. This endpoint queries the blockchain for multiple wallets in a single request, which is more efficient than making individual requests. The response is a record mapping each public key to its balance information (total balance, hex balance, UTXO count, decimals) without individual UTXO details. This is ideal when you only need balance summaries for multiple wallets and don't require UTXO-level information.
 * @summary Batch get CWEB wallet balances without UTXO details
 */
export const batchGetBalance = (params: BatchGetBalanceParams, signal?: AbortSignal) => {
  return orvalMutator<BatchGetBalance200>({
    url: `/ecdsa_cm/batchGetWalletBalanceCWEB`,
    method: 'GET',
    params,
    signal,
  });
};

export const getBatchGetBalanceQueryKey = (params?: BatchGetBalanceParams) => {
  return [`/ecdsa_cm/batchGetWalletBalanceCWEB`, ...(params ? [params] : [])] as const;
};

export const getBatchGetBalanceQueryOptions = <TData = Awaited<ReturnType<typeof batchGetBalance>>, TError = unknown>(
  params: BatchGetBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalance>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBatchGetBalanceQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof batchGetBalance>>> = ({ signal }) =>
    batchGetBalance(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof batchGetBalance>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BatchGetBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof batchGetBalance>>>;
export type BatchGetBalanceQueryError = unknown;

export function useBatchGetBalance<TData = Awaited<ReturnType<typeof batchGetBalance>>, TError = unknown>(
  params: BatchGetBalanceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalance>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof batchGetBalance>>,
          TError,
          Awaited<ReturnType<typeof batchGetBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBatchGetBalance<TData = Awaited<ReturnType<typeof batchGetBalance>>, TError = unknown>(
  params: BatchGetBalanceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalance>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof batchGetBalance>>,
          TError,
          Awaited<ReturnType<typeof batchGetBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBatchGetBalance<TData = Awaited<ReturnType<typeof batchGetBalance>>, TError = unknown>(
  params: BatchGetBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Batch get CWEB wallet balances without UTXO details
 */

export function useBatchGetBalance<TData = Awaited<ReturnType<typeof batchGetBalance>>, TError = unknown>(
  params: BatchGetBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof batchGetBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBatchGetBalanceQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the fee pool balance (CWEB tokens) for a specific owner address, typically a smart contract address. The fee pool collects fees from swap transactions and other protocol operations. This endpoint queries the blockchain to get the current balance of CWEB tokens held by the specified contract address. If no address is provided, it defaults to the standard fee pool contract address. The balance is returned in the smallest unit (wei) as a string representation of a bigint.
 * @summary Get fee pool balance for an owner address
 */
export const getFeePoolBalance = (params?: GetFeePoolBalanceParams, signal?: AbortSignal) => {
  return orvalMutator<GetFeePoolBalance200>({ url: `/owner_cm/getFeePoolBalance`, method: 'GET', params, signal });
};

export const getGetFeePoolBalanceQueryKey = (params?: GetFeePoolBalanceParams) => {
  return [`/owner_cm/getFeePoolBalance`, ...(params ? [params] : [])] as const;
};

export const getGetFeePoolBalanceQueryOptions = <
  TData = Awaited<ReturnType<typeof getFeePoolBalance>>,
  TError = unknown,
>(
  params?: GetFeePoolBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolBalance>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFeePoolBalanceQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFeePoolBalance>>> = ({ signal }) =>
    getFeePoolBalance(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getFeePoolBalance>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetFeePoolBalanceQueryResult = NonNullable<Awaited<ReturnType<typeof getFeePoolBalance>>>;
export type GetFeePoolBalanceQueryError = unknown;

export function useGetFeePoolBalance<TData = Awaited<ReturnType<typeof getFeePoolBalance>>, TError = unknown>(
  params: undefined | GetFeePoolBalanceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolBalance>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFeePoolBalance>>,
          TError,
          Awaited<ReturnType<typeof getFeePoolBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFeePoolBalance<TData = Awaited<ReturnType<typeof getFeePoolBalance>>, TError = unknown>(
  params?: GetFeePoolBalanceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolBalance>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFeePoolBalance>>,
          TError,
          Awaited<ReturnType<typeof getFeePoolBalance>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFeePoolBalance<TData = Awaited<ReturnType<typeof getFeePoolBalance>>, TError = unknown>(
  params?: GetFeePoolBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get fee pool balance for an owner address
 */

export function useGetFeePoolBalance<TData = Awaited<ReturnType<typeof getFeePoolBalance>>, TError = unknown>(
  params?: GetFeePoolBalanceParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFeePoolBalance>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFeePoolBalanceQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getLpCmGetLPList = (params: GetLpCmGetLPListParams, signal?: AbortSignal) => {
  return orvalMutator<GetLpCmGetLPList200>({ url: `/lp_cm/getLPList`, method: 'GET', params, signal });
};

export const getGetLpCmGetLPListQueryKey = (params?: GetLpCmGetLPListParams) => {
  return [`/lp_cm/getLPList`, ...(params ? [params] : [])] as const;
};

export const getGetLpCmGetLPListQueryOptions = <TData = Awaited<ReturnType<typeof getLpCmGetLPList>>, TError = unknown>(
  params: GetLpCmGetLPListParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLpCmGetLPList>>, TError, TData>> }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLpCmGetLPListQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLpCmGetLPList>>> = ({ signal }) =>
    getLpCmGetLPList(params, signal);

  return { queryKey, queryFn, staleTime: 60000, refetchOnWindowFocus: true, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLpCmGetLPList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLpCmGetLPListQueryResult = NonNullable<Awaited<ReturnType<typeof getLpCmGetLPList>>>;
export type GetLpCmGetLPListQueryError = unknown;

export function useGetLpCmGetLPList<TData = Awaited<ReturnType<typeof getLpCmGetLPList>>, TError = unknown>(
  params: GetLpCmGetLPListParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLpCmGetLPList>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLpCmGetLPList>>,
          TError,
          Awaited<ReturnType<typeof getLpCmGetLPList>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLpCmGetLPList<TData = Awaited<ReturnType<typeof getLpCmGetLPList>>, TError = unknown>(
  params: GetLpCmGetLPListParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLpCmGetLPList>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLpCmGetLPList>>,
          TError,
          Awaited<ReturnType<typeof getLpCmGetLPList>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLpCmGetLPList<TData = Awaited<ReturnType<typeof getLpCmGetLPList>>, TError = unknown>(
  params: GetLpCmGetLPListParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLpCmGetLPList>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetLpCmGetLPList<TData = Awaited<ReturnType<typeof getLpCmGetLPList>>, TError = unknown>(
  params: GetLpCmGetLPListParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLpCmGetLPList>>, TError, TData>> },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetLpCmGetLPListQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
